/**
 * 計算フィールド 計算エンジン（#3145）の定数定義。
 * ⚠️ functions/src/utils/calculation と main/src/utils/calculation は同一内容。
 *    片方だけ変更しないこと（変更時は両方へ反映し、共通テストで一致を確認する）。
 */

/** 計算式の最大文字数（#3145） */
export const CALCULATION_FORMULA_MAX_LENGTH = 500;
/** 正規表現パターンの最大文字数（ReDoS 対策） */
export const CALCULATION_REGEX_MAX_LENGTH = 200;
/** 計算対象文字列の最大文字数（ReDoS 対策） */
export const CALCULATION_TEXT_MAX_LENGTH = 10000;
/** 計算フィールドの依存の深さ上限 */
export const CALCULATION_MAX_DEPENDENCY_DEPTH = 20;
/** 1 パターンあたりの計算フィールド数の上限 */
export const CALCULATION_FIELD_MAX_COUNT = 20;
/** 数値を文字列化する際の有効桁数（浮動小数点誤差の丸め用） */
export const CALCULATION_RESULT_SIGNIFICANT_DIGITS = 15;

/**
 * 丸め関数（ROUND / ROUNDDOWN / ROUNDUP）の桁数の指定範囲。
 * 10 のべき乗を作るため、これを超えると内部計算が Infinity / 0 に潰れて結果が壊れる。
 */
export const CALCULATION_ROUND_DIGITS_LIMIT = 15;

/** 計算式で使える関数名 */
export const calculationFunction = {
  left: 'LEFT',
  right: 'RIGHT',
  mid: 'MID',
  replace: 'REPLACE',
  match: 'MATCH',
  asc: 'ASC',
  if: 'IF',
  and: 'AND',
  or: 'OR',
  not: 'NOT',
  sum: 'SUM',
  round: 'ROUND',
  roundDown: 'ROUNDDOWN',
  roundUp: 'ROUNDUP',
} as const;

export type CalculationFunctionName =
  (typeof calculationFunction)[keyof typeof calculationFunction];

/**
 * 計算式で使える関数の引数の数（#3358 で範囲指定に変更）。
 * max を省略すると可変長（SUM のように引数をいくつでも取れる）。
 */
export interface CalculationFunctionArity {
  /** 最小引数数 */
  min: number;
  /** 最大引数数。可変長なら undefined */
  max?: number;
}

export const calculationFunctionArity: Record<CalculationFunctionName, CalculationFunctionArity> = {
  LEFT: { min: 2, max: 2 },
  RIGHT: { min: 2, max: 2 },
  MID: { min: 3, max: 3 },
  REPLACE: { min: 3, max: 3 },
  MATCH: { min: 2, max: 2 },
  // 全角→半角変換（#3359）。変換対象の文字列だけを取る
  ASC: { min: 1, max: 1 },
  // 条件分岐（#3358）。IF(条件, 真のときの値, 偽のときの値)
  IF: { min: 3, max: 3 },
  // 論理演算（#3358）。AND / OR は条件をいくつでも取れる（可変長）。
  // 「入数が空 かつ 数値が空」のような複数条件を IF の入れ子にせず書けるようにする
  AND: { min: 2 },
  OR: { min: 2 },
  // NOT は条件を1つだけ反転する
  NOT: { min: 1, max: 1 },
  // 明細の集計（#3358）。SUM({明細.金額}) のように集約参照を取る。可変長
  SUM: { min: 1 },
  // 丸め関数の桁数は省略できない（Excel の ROUND と同じ）
  ROUND: { min: 2, max: 2 },
  ROUNDDOWN: { min: 2, max: 2 },
  ROUNDUP: { min: 2, max: 2 },
};

/** 関数の引数の数の上限（#3358）。無制限だと評価コストの見積もりが立たない */
export const CALCULATION_FUNCTION_MAX_ARGS = 50;

/** 計算エラーの種別 */
export const calculationErrorCode = {
  syntax: 'syntax',
  unknownField: 'unknown_field',
  outOfScope: 'out_of_scope',
  circular: 'circular',
  notNumber: 'not_number',
  divideByZero: 'divide_by_zero',
  invalidRegex: 'invalid_regex',
  limitExceeded: 'limit_exceeded',
  // 集約参照（#3358）
  aggregateOutOfScope: 'aggregate_out_of_scope', // SUM 以外で集約参照を使った
  aggregateNotFound: 'aggregate_not_found', // 集約参照の親子が解決できない
} as const;

export type CalculationErrorCode = (typeof calculationErrorCode)[keyof typeof calculationErrorCode];

/** 計算警告の種別 */
export const calculationWarningCode = {
  dangerousRegex: 'dangerous_regex',
} as const;

export type CalculationWarningCode =
  (typeof calculationWarningCode)[keyof typeof calculationWarningCode];

/** 計算エラー種別ごとのメッセージ */
export const calculationErrorMessage: Record<CalculationErrorCode, string> = {
  syntax: '計算式に誤りがあります',
  unknown_field: '存在しない項目を参照しています',
  out_of_scope: '参照できない項目を参照しています',
  circular: '循環参照があります',
  not_number: '数値に変換できない値があります',
  divide_by_zero: '0で割ることはできません',
  invalid_regex: '正規表現が正しくありません',
  limit_exceeded: '計算式の上限を超えています',
  aggregate_out_of_scope: '明細の集計は SUM の中でだけ使えます',
  aggregate_not_found: '集計する明細の項目が見つかりません',
};

/** 計算警告種別ごとのメッセージ */
export const calculationWarningMessage: Record<CalculationWarningCode, string> = {
  dangerous_regex: '正規表現に処理が極端に遅くなる可能性のあるパターンが含まれています',
};
